import { convertFormDataToDictionary, Dictionary } from './forms';

type SubmitHandler = (data: FormData) => boolean;
type ResponseHandler = (response: Response) => boolean;
type FormElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement;

export const customFetch = (input: RequestInfo, init?: RequestInit): Promise<Response> =>
    fetch(input, init)
        .then(response => {
            if (!response.ok || response.redirected)
            {
                console.error(`Unexpected response code: ${response.status} for ${response.url}`);
                location.href = '/error/ajax';
            }
            return response;
        })
        .catch(error => {
            console.error(error);
            location.href = '/error/ajax';
            throw error;
        });

export function makeAjaxForm(element: HTMLFormElement, submitHandler: SubmitHandler, responseHandler: ResponseHandler)
{
    element.addEventListener('submit', submitEvent => {
        // Prevent the normal form submission
        submitEvent.preventDefault();

        const form = submitEvent.currentTarget as HTMLFormElement;

        // Retrieve the CSRF token from the meta tag
        const csrfToken = document.querySelector('meta[name=\'_csrf\']').getAttribute('content');
        const csrfHeader = document.querySelector('meta[name=\'_csrf_header\']').getAttribute('content');

        // Get Info for AJAX from form
        const url = form.getAttribute('action');
        const method = form.getAttribute('method');
        const formData = new FormData(form);

        // Do not include _csrf in form
        if (formData.has('_csrf'))
        {
            formData.delete('_csrf');
        }

        // Check for invalid inputs
        const invalidInputs = form.querySelectorAll('input:invalid');
        if (invalidInputs.length > 0)
        {
            return;
        }

        // Call submit handler on form data
        if (submitHandler)
        {
            if (!submitHandler(formData))
            {
                return;
            }
        }

        // Disable all button(s)
        const buttons = form.querySelectorAll('button');
        for (const submitButton of buttons)
        {
            submitButton.disabled = true;
        }

        // Include CSRF header
        let headers = {
            [csrfHeader]: csrfToken,
            ['Content-Type']: 'application/json'
        };

        // Convert FormData to ordinary object before serializing to JSON
        const formDataDictionary: Dictionary = convertFormDataToDictionary(formData);
        const body = JSON.stringify(formDataDictionary);

        // Send AJAX request
        customFetch(url, {
            body,
            headers,
            method
        })
        .then(response => {
            // Re-enable submit button(s)
            for (const submitButton of buttons)
            {
                submitButton.disabled = false;
            }

            return response;
        })
        .then(response => {
            if (response.status !== 200)
            {
                location.href = '/error/ajax';
            }
            return response;
        })
        // Handle the response
        .then(responseHandler);
    })
}

export function makeAjaxFileForm(element: HTMLFormElement, submitHandler: SubmitHandler, responseHandler: ResponseHandler)
{
    element.addEventListener('submit', submitEvent => {
        // Prevent the normal form submission
        submitEvent.preventDefault();

        const form = submitEvent.currentTarget as HTMLFormElement;

        // Retrieve the CSRF token from the meta tag
        const csrfToken = document.querySelector('meta[name=\'_csrf\']').getAttribute('content');
        const csrfHeader = document.querySelector('meta[name=\'_csrf_header\']').getAttribute('content');

        // Get Info for AJAX from form
        const url = form.getAttribute('action');
        const method = form.getAttribute('method');

        const formData = new FormData();

        // Do not include _csrf in form
        if (formData.has('_csrf'))
        {
            formData.delete('_csrf');
        }

        // Include CSRF header
        let headers = { [csrfHeader]: csrfToken };

        // Add file data
        const fileInput = form.querySelector('input[type=\'file\']') as HTMLInputElement;
        const file = fileInput.files[0];

        if (!file)
        {
            return;
        }

        formData.append('file', file);

        // Call submit handler on form data
        if (submitHandler)
        {
            if (!submitHandler(formData))
            {
                return;
            }
        }

        // Disable submit button(s)
        const buttons = form.querySelectorAll('button');
        for (const submitButton of buttons)
        {
            submitButton.disabled = true;
        }

        const body = formData;

        // Send AJAX request
        customFetch(url, {
            body,
            headers,
            method
        })
        .then(response => {
            // Re-enable submit button(s)
            for (const submitButton of buttons)
            {
                submitButton.disabled = false;
            }
            return response;
        })
        // Handle the response
        .then(responseHandler);
    })
}

export function submitForm(form: HTMLFormElement)
{
    const event = new Event('submit', {
        bubbles: true,
        cancelable: true
    });

    form.dispatchEvent(event);
}

export function disableForm(form: HTMLFormElement)
{
    const elements = Array.from(form.elements) as FormElement[];

    for (const element of elements)
    {
        element.disabled = true;
    }
}
